import { forwardRef, ImgHTMLAttributes, MouseEventHandler, useId, useRef, useState } from 'react'
import classNames from 'classnames'
import brokenImage from './ImageBroken'
import { FloatingMenu, ImageResizeMode, MenuHeader, MenuItem } from '..'
import { useMergeRefs } from '@floating-ui/react'

// TODO: temporary workaround for missing cookie intermittent img download issue
const BAND_AID_RETRY_ATTEMPTS = 5

export interface ImageProps extends ImgHTMLAttributes<HTMLOrSVGImageElement> {
    url: string
    fileName?: string
    className?: string
    flexibility?: 'fix-fluid' | 'fluid' | 'fixed'
    rounded?: boolean
    thumbnail?: boolean
    resizeMode?: ImageResizeMode
    width?: number | string
    height?: number | string
    variant?: 'tile' | 'file-list' | 'file-attachment' | 'preview'
    enableContextMenu?: boolean
    onDownload?: () => void
}

const Image = forwardRef<HTMLImageElement, ImageProps>(({
    url,
    fileName,
    className,
    flexibility = 'fix-fluid',
    rounded,
    thumbnail,
    resizeMode,
    height,
    width,
    enableContextMenu = false,
    onDownload = () => { /** no-op */ },
    ...otherProps
}, ref) => {

    const errorCount = useRef(0)
    const [imgKey, setImgKey] = useState(0)

    const contextMenuRef = useRef<HTMLDivElement>(null)

    const dimensions = {
        //have to have px for css calc()/max() to work
        width: !width ? '400px' : `${Number(width)}px`,
        height: !height ? '400px' : `${Number(height)}px`
    }

    const _className = classNames({
        'd-block': true,
        'img-fluid': flexibility === 'fluid',
        'rounded': rounded,
        'img-thumbnail': thumbnail,
        [`object-fit-${resizeMode}`]: resizeMode
    },
    (otherProps.variant === 'tile') ? className : ''
    )

    const styles = {
        ...(flexibility === 'fix-fluid' && {
            maxWidth: `min(${dimensions.width}, 100%)`,
            maxHeight: `min(${dimensions.height}, calc(100vh - 200px))`
        }),
        ...{
            width: `${dimensions.width}`,
            height: `${dimensions.height}`
        }
    }

    const onImageError = e => {
        if (errorCount.current < BAND_AID_RETRY_ATTEMPTS) {
            // if we rerender it might cause src={url} to revert, so using useRef

            errorCount.current++

            // TODO: temporary workaround for missing cookie intermittent img download issue
            const sep = url.includes('?') ? '&' : '?'
            e.target.src = `${url}${sep}e=${errorCount.current}`

        } else {
            e.target.src = brokenImage
        }
    }

    const handleContextMenu = (e: React.MouseEvent<HTMLElement>) => {
        if (!enableContextMenu) {
            e.preventDefault()
            e.stopPropagation()
        }
    }

    const renderToolTip = otherProps.variant === 'preview'

    const htmlId = useId()
    const image = (
        <div id={htmlId} ref={contextMenuRef} style={{display: 'contents'}}>
            <img
                key={imgKey}
                ref={ref}
                src={url}
                style={styles}
                onError={onImageError}
                className={_className}
                alt={fileName}
                width={null}
                height={null}
                {...otherProps}
                {...(!enableContextMenu && { onContextMenu: handleContextMenu })}
            />
            {enableContextMenu && (
                <FloatingMenu contextTarget={contextMenuRef} attachId={htmlId}>
                    <MenuHeader label={fileName || 'Media Element'} />
                    <MenuItem
                        label="Download"
                        icon={{ name: 'cloud-arrow-down' }}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onDownload()
                        }}
                    />
                </FloatingMenu>
            )}
        </div>
    )

    return renderToolTip ?
        <div data-tooltip={fileName || 'No file name'} data-tooltip-pos="top">
            {image}
        </div>
        :
        image
})

export { Image }
